import { useState, useMemo } from 'react'
import { Plus, Edit2, Trash2, Network, Users } from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { setDocument } from '../../services/firebase'
import { slugify } from '../../utils/helpers'
import { Button, Modal, Input, Textarea, Badge, Checkbox } from '../../components/common'

const DepartmentForm = ({ department, leaderCandidates, onSave, onClose }) => {
  const [formData, setFormData] = useState(department || {
    name: '',
    description: '',
    leaderName: '',
    leaderUids: [],
    order: 0,
    isActive: true,
  })
  const [saving, setSaving] = useState(false)

  const leaderUids = formData.leaderUids || []
  // Leaders no longer in the department stay visible so admins can remove them
  const staleLeaderUids = leaderUids.filter(
    (uid) => !leaderCandidates.some((w) => w.id === uid)
  )

  const toggleLeader = (uid) => {
    setFormData({
      ...formData,
      leaderUids: leaderUids.includes(uid)
        ? leaderUids.filter((u) => u !== uid)
        : [...leaderUids, uid],
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onSave(formData)
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Department Name"
        required
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="Choir"
      />
      {!department && formData.name && (
        <p className="text-sm text-gray-500 -mt-4">
          URL: /workers/departments/{slugify(formData.name)}
        </p>
      )}

      <Textarea
        label="Description"
        rows={3}
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        placeholder="What this department does..."
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Leader Name (optional)"
          value={formData.leaderName}
          onChange={(e) => setFormData({ ...formData, leaderName: e.target.value })}
        />
        <Input
          label="Sort Order"
          type="number"
          value={formData.order}
          onChange={(e) => setFormData({ ...formData, order: e.target.value })}
        />
      </div>

      {department && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">Department Leaders</p>
          <p className="text-xs text-gray-500 mb-2">
            Leaders can manage songs and song lists for this department from the
            workers portal.
          </p>
          <div className="space-y-2">
            {leaderCandidates.map((w) => (
              <Checkbox
                key={w.id}
                label={w.displayName || w.email}
                checked={leaderUids.includes(w.id)}
                onChange={() => toggleLeader(w.id)}
              />
            ))}
            {staleLeaderUids.map((uid) => (
              <Checkbox
                key={uid}
                label={`${uid.slice(0, 8)}… (no longer in department)`}
                checked
                onChange={() => toggleLeader(uid)}
              />
            ))}
            {leaderCandidates.length === 0 && staleLeaderUids.length === 0 && (
              <p className="text-sm text-gray-500">
                No approved members in this department yet — approve workers into
                it first (Workers page).
              </p>
            )}
          </div>
        </div>
      )}

      <Checkbox
        label="Active"
        checked={formData.isActive}
        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
      />

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {department ? 'Update Department' : 'Create Department'}
        </Button>
      </div>
    </form>
  )
}

const DepartmentsManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { data: departments, update, remove } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })
  const { data: workers } = useCollection('users', {
    orderByField: null,
    whereClause: { field: 'role', operator: '==', value: 'worker' },
  })

  const sorted = useMemo(
    () => [...(departments || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [departments]
  )

  const memberCount = (slug) =>
    (workers || []).filter(
      (w) => w.status === 'approved' && (w.departmentIds || []).includes(slug)
    ).length

  const handleSave = async (data) => {
    const payload = {
      ...data,
      order: Number(data.order) || 0,
    }
    if (editing?.id) {
      await update(editing.id, payload)
    } else {
      // Doc id IS the slug so /workers/departments/:slug reads it directly
      const slug = slugify(data.name)
      await setDocument('departments', slug, { ...payload, slug, leaderUids: [] })
    }
    setShowModal(false)
    setEditing(null)
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      await remove(deleteConfirm)
      setDeleteConfirm(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Departments</h1>
          <p className="text-gray-500">Worker departments for the portal (choir, ushers, media...)</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditing(null)
            setShowModal(true)
          }}
        >
          Add Department
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Department</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Leader</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Members</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((dept) => (
              <tr key={dept.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <p className="font-medium text-dark-100">{dept.name}</p>
                  <p className="text-sm text-gray-500">/{dept.id}</p>
                </td>
                <td className="px-6 py-4 text-gray-600">{dept.leaderName || '—'}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1 text-gray-600">
                    <Users className="w-4 h-4" />
                    {memberCount(dept.id)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Badge variant={dept.isActive ? 'success' : 'gray'} size="sm">
                    {dept.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        setEditing(dept)
                        setShowModal(true)
                      }}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(dept.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sorted.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Network className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No departments yet — add Choir, Ushers, Media, Prayer...</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditing(null)
        }}
        title={editing ? 'Edit Department' : 'Create Department'}
      >
        <DepartmentForm
          department={editing}
          leaderCandidates={(workers || []).filter(
            (w) =>
              w.status === 'approved' &&
              (w.departmentIds || []).includes(editing?.id)
          )}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditing(null)
          }}
        />
      </Modal>

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Department"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Delete this department? Workers assigned to it will keep their accounts
          but lose this department page.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default DepartmentsManager
